import { ref, watch } from "vue";
import { type AgentSession, useDeleteSession } from "../api/use-agents";
import type { AgentDoor } from "../api/use-ben";
import { useRememberedSession } from "./use-remembered-session";

/**
 * EM QUE CONVERSA A DOCA ESTÁ — e as duas memórias são diferentes de propósito.
 *
 * ── NO PAINEL: lembrada, e POR AGENTE ─────────────────────────────────────
 *
 * A doca escolhia a thread no INSTANTE do clique: uma leitura única de uma
 * consulta que podia ainda estar em voo. Depois de um F5 o ponteiro ficava `null`
 * e o envio seguinte abria uma conversa NOVA — a anterior ficava órfã na lateral.
 * O relato do dono foi "a mensagem some ao reabrir": as falas estavam gravadas,
 * mas a janela deixara de saber onde. `localStorage`, porque um `ref` não
 * sobrevive a um recarregamento — que é o momento em que isto dói. Por AGENTE,
 * porque a conversa pertence a quem a produziu.
 *
 * ── NA CONVERSA: não se lembra, PERGUNTA ──────────────────────────────────
 *
 * A thread daquela conversa é um facto do servidor (uma por pessoa por assunto,
 * índice único), e ele já a devolveu na mesma resposta que disse quem é o Ben.
 * Lembrá-la aqui seria uma segunda cópia de algo que não é desta máquina — e a
 * cópia ficaria velha no instante em que outro separador arquivasse a thread.
 *
 * ── POR QUE "NOVA CONVERSA" ARQUIVA, NA PORTA DA CONVERSA ─────────────────
 *
 * Porque não há como recomeçar de outra maneira: abrir uma sessão com o mesmo
 * assunto devolve a MESMA (o `ON CONFLICT DO NOTHING` do repositório), então
 * limpar só a tela seria uma tela a mentir — a thread continuaria lá e voltaria
 * na recarga seguinte. Arquivar é o único "zerar" honesto, e por isso pede o
 * segundo clique.
 */
export function useBenThread(input: {
	door: () => AgentDoor;
	agentCode: () => string | null;
	/** A thread daquele assunto, como o servidor a devolveu. `null` na doca. */
	fromServer: () => AgentSession | null;
}) {
	const remembered = useRememberedSession();
	const archive = useDeleteSession();

	const sessionId = ref<string | null>(null);
	/** "Nova conversa" à espera do segundo clique — só na porta da conversa. */
	const armed = ref(false);

	watch(
		[
			input.door,
			input.agentCode,
			() => input.fromServer()?.id ?? null,
			() => remembered.sessionFor(input.agentCode()),
		],
		([door, _code, anchored, panel]) => {
			sessionId.value = door === "conversation" ? anchored : panel;
			armed.value = false;
		},
		{ immediate: true },
	);

	/** Passa a apontar para esta conversa, e — na doca — a lembrá-la na próxima visita. */
	function pointAt(id: string | null): void {
		sessionId.value = id;
		const code = input.agentCode();
		if (input.door() === "panel" && code && id) remembered.remember(code, id);
	}

	/**
	 * Recomeçar. Na doca é uma escolha barata; na conversa custa a thread.
	 *
	 * Devolve `true` quando alguma coisa aconteceu, e `false` quando ficou só
	 * armado — quem chama não precisa saber qual dos dois casos foi, mas o teste
	 * precisa.
	 */
	async function startNew(): Promise<boolean> {
		const current = sessionId.value;
		if (input.door() === "conversation") {
			if (!current) return true;
			if (!armed.value) {
				armed.value = true;
				return false;
			}
			await archive.mutateAsync(current).catch(() => null);
			armed.value = false;
			sessionId.value = null;
			return true;
		}
		// Uma conversa nova é uma ESCOLHA, e por isso apaga o que estava lembrado:
		// senão a doca voltaria à antiga na próxima abertura.
		if (current) remembered.forget(input.agentCode(), current);
		sessionId.value = null;
		return true;
	}

	/** A conversa apagada pode ser a que está aberta; cair para `null` faz a próxima
	 *  mensagem começar uma nova em vez de escrever numa sessão que não existe mais. */
	function onRemoved(id: string): void {
		remembered.forget(input.agentCode(), id);
		if (sessionId.value === id) sessionId.value = null;
	}

	return {
		sessionId,
		armed,
		pointAt,
		startNew,
		onRemoved,
		sessionsOf: remembered.sessionsOf,
	};
}
